import { z } from 'zod'
import { tier } from '../subscription/tier'
import { period } from '../subscription/period'
import { column } from '../campaign/column'
import { paginatedSearchQuery } from '../zod/pagination'

export const companySubscription = z.object({
    id: z.union([z.string(), z.number()]),
    type: z.string(),
    active: z.boolean(),
    priceId: z.string().nullable().optional(),
    tier: tier.nullable().optional(),
    period: period.nullable().optional(),
    endsAt: z.coerce.date().nullable(),
})

export const company = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    address: z.string().nullable(),
    vatNumber: z.string().nullable(),
    city: z.string().nullable(),
    postalCode: z.string().nullable(),
    pmType: z.string().nullable().optional(),
    pmLastFour: z.string().nullable().optional(),
    tier: tier.nullable().optional(),
    period: period.nullable().optional(),
    overagesEnabled: z.boolean().optional(),
    overageLimit: z.number().nullable().optional(),
    messageOveragesEnabled: z.boolean().optional(),
    messageOverageLimit: z.number().nullable().optional(),
    isAgency: z.boolean(),
    conversationLimit: z.number().nullable().optional(),
    userLimit: z.number().nullable().optional(),
    siteLimit: z.number().nullable().optional(),
    messageLimit: z.number().nullable().optional(),
    agencyPeriod: period.nullable().optional(),
    agencyBaseUnitAmount: z.number().nullable().optional(),
    agencyOverageUnitAmount: z.number().nullable().optional(),
    stripeId: z.string().nullable().optional(),
    facebookConnected: z.boolean(),
    usersCount: z.number().optional(),
    campaignsCount: z.number().optional(),
    candidatesCount: z.number().optional(),
    subscriptions: companySubscription.array().optional(),
    columns: column.array(),
    trialEndsAt: z.coerce.date().optional().nullable(),
    paymentFailing: z.boolean().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
})

export type Company = z.infer<typeof company>

export const companyUsage = z.object({
    usage: z.number(),
    limit: z.number(),
    overagesEnabled: z.boolean(),
    overageLimit: z.number().nullable(),
    overageUsage: z.number(),
    overageUnitAmount: z.number(),
    // When the current billing period ends; included usage starts again then.
    resetsAt: z.coerce.date(),
    // Website visitor messages in this billing period over all websites, and the websites in use.
    messages: z.object({
        usage: z.number(),
        allowance: z.number(),
        extraUsage: z.number(),
        overagesEnabled: z.boolean(),
        overageLimit: z.number().nullable(),
        overageUnitAmount: z.number(),
        // The limits a customer can choose for extra messages: amount in cents excl. VAT, and the messages it buys.
        overageSteps: z.array(z.object({ amount: z.number(), messages: z.number() })),
    }),
    sites: z.object({
        connected: z.number(),
        limit: z.number(),
        extra: z.number(),
    }),
    // One reason from this usage to upgrade to the next plan, for the top of the upgrade window.
    upgradeReason: z
        .object({ key: z.string(), tier: z.string(), values: z.record(z.string(), z.number()) })
        .nullable()
        .optional(),
})

export type CompanyUsage = z.infer<typeof companyUsage>

export const companyQuery = paginatedSearchQuery({
    filters: z.object({
        name: z.string().array().optional(),
        email: z.string().array().optional(),
    }),
    sort: z.enum([
        'name',
        '-name',
        'email',
        '-email',
        'tier',
        '-tier',
        'created_at',
        '-created_at',
        'updated_at',
        '-updated_at',
    ]),
})

export type CompanyQuery = z.infer<typeof companyQuery>
